import type React from "react";
import type {AlertProps} from "@mui/material";
import {makeAutoObservable} from "mobx";

type MessageOptions = {
    id?: string,
    severity?: AlertProps['severity'],
    actionText?: React.ReactNode,
    onAction?: () => void,
    /** Auto-dismiss after this many ms. Omit to keep until manually closed. */
    autoHideDuration?: number,
}

type Message = {
    id: string
    message: React.ReactNode
    /**
     * Whether the message is closed or not
     */
    closed: boolean
    /**
     * Whether the message is temporary hidden or not
     */
    hidden: boolean

} & MessageOptions;

export class NotificationStore {
    public static readonly CATEGORIZATION_MSG_ID = 'categorization-update';

    static _ids = 0;
    queue: Message[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * Show a message
     * Note: this does not change the order of the messages
     */
    show(message: React.ReactNode, options?: MessageOptions, forceUnhide = false): Message['id'] {
        const id = options?.id ?? String(NotificationStore._ids++);
        const index = this.queue.findIndex(m => m.id === id);
        if (index === -1) {
            const newMsg: Message = {
                message,
                closed: false,
                hidden: false,
                ...options,
                id,
            }
            this.queue.unshift(newMsg);
        } else {
            const prevMsg = this.queue[index];
            prevMsg.closed = false;
            if (forceUnhide) {
                prevMsg.hidden = false;
            }
            this.queue[index] = {
                ...prevMsg,
                ...options,
                message,
            }
        }
        return id;
    }

    hide(id: Message['id']) {
        const m = this.queue.find(m => m.id === id);
        if (m) m.closed = true;
    }

    temporaryHide(id: Message['id']) {
        const m = this.queue.find(m => m.id === id);
        if (m) m.hidden = true;
    }

    temporaryUnHide(id: Message['id']) {
        const m = this.queue.find(m => m.id === id);
        if (m) m.hidden = false;
    }

    get active(): Message | null {
        return this.queue.find(m => !m.closed && !m.hidden)
            ?? this.queue.at(-1) // Show the correct text when fading away
            ?? null;
    }
}